import {
  createContext,
  useContext,
  useMemo,
  cloneElement,
  isValidElement,
  type ReactNode,
  type ReactElement,
} from "react";
import { useAccessControl, useFieldControl } from "../query/config-queries";
import { useAuthStore } from "../stores/auth-store";
import type { AccessControlRule, FieldControlRule } from "../types/auth";

// --- Module Context ---

const ModuleContext = createContext<string | null>(null);

export interface AccessControlProviderProps {
  module: string;
  children: ReactNode;
}

/**
 * Sets the current module context for all `AccessGate` components
 * within its subtree. Typically placed at the page/container level.
 */
export function AccessControlProvider({
  module,
  children,
}: AccessControlProviderProps): ReactElement {
  return (
    <ModuleContext.Provider value={module}>{children}</ModuleContext.Provider>
  );
}

// --- Permission Resolution ---

type ElementState = "visible" | "hidden" | "disabled";

function resolveFieldControlDefault(
  rules: FieldControlRule[],
  module: string,
  elementId: string,
): ElementState | null {
  const rule = rules.find((r) => r.module === module && r.field === elementId);
  if (!rule) return null;

  switch (rule.fieldDefault) {
    case "hide":
      return "hidden";
    case "readonly":
      return "disabled";
    case "show":
      return "visible";
    default:
      return null;
  }
}

function resolveAccessControlOverride(
  rules: AccessControlRule[],
  profile: string,
  module: string,
  elementId: string,
): ElementState | null {
  // When multiple rules match, use the last one (per spec FR-021)
  const matching = rules.filter(
    (r) =>
      r.profile === profile &&
      r.module === module &&
      r.accessLevel === elementId,
  );
  if (matching.length === 0) return null;

  const rule = matching[matching.length - 1];

  // wobpos semantics (apply_controlAccess in common_js.ejs):
  //   edit === 'T'  → readonly  (disabled)
  //   edit === 'F'  → editable  (visible)
  //   hide takes precedence over edit when both are set.
  if (rule.hide) return "hidden";
  if (rule.edit) return "disabled";
  return "visible";
}

/**
 * Resolves the three-layer permission for an element:
 * 1. Field control default (global)
 * 2. Access control override (per-profile)
 * 3. Fallback to visible+editable
 */
function resolveElementState(
  fieldControlRules: FieldControlRule[],
  accessControlRules: AccessControlRule[],
  profile: string,
  module: string,
  elementId: string,
  defaultState: ElementState = "visible",
): ElementState {
  // Layer 2: Field control defaults (global, not per-profile)
  const fieldDefault = resolveFieldControlDefault(
    fieldControlRules,
    module,
    elementId,
  );

  // Layer 3: Access control override (per-profile)
  const accessOverride = resolveAccessControlOverride(
    accessControlRules,
    profile,
    module,
    elementId,
  );

  // Access control overrides field control when both apply
  if (accessOverride !== null) return accessOverride;
  if (fieldDefault !== null) return fieldDefault;

  // Fallback to caller-specified default
  return defaultState;
}

// --- Hook ---

/**
 * Returns the resolved visibility state ("visible" | "hidden" | "disabled")
 * for a given element within the current module context.
 */
export function useElementState(
  elementId: string,
  defaultHidden = false,
): ElementState {
  const module = useContext(ModuleContext);
  const userType = useAuthStore((s) => s.user?.userType);
  const accessControlRules = useAccessControl();
  const fieldControlRules = useFieldControl();

  return useMemo(() => {
    if (!module || !userType) {
      return defaultHidden ? ("hidden" as const) : ("visible" as const);
    }
    return resolveElementState(
      fieldControlRules,
      accessControlRules,
      userType,
      module,
      elementId,
      defaultHidden ? "hidden" : "visible",
    );
  }, [
    module,
    userType,
    fieldControlRules,
    accessControlRules,
    elementId,
    defaultHidden,
  ]);
}

// --- AccessGate Component ---

export interface AccessGateProps {
  elementId: string;
  /** When true, element is hidden unless access control explicitly shows it. */
  defaultHidden?: boolean;
  children: ReactNode;
}

/**
 * Wraps a UI element and applies access control:
 * - Hidden: returns null (removed from DOM)
 * - Disabled: clones children with disabled/readOnly props (form elements)
 *   or wraps with pointer-events:none + aria-disabled (non-form elements)
 * - Visible: renders children as-is
 */
export function AccessGate({
  elementId,
  defaultHidden = false,
  children,
}: AccessGateProps): ReactElement | null {
  const module = useContext(ModuleContext);
  const userType = useAuthStore((s) => s.user?.userType);
  const accessControlRules = useAccessControl();
  const fieldControlRules = useFieldControl();

  const state = useMemo(() => {
    if (!module || !userType) {
      return defaultHidden ? ("hidden" as const) : ("visible" as const);
    }

    return resolveElementState(
      fieldControlRules,
      accessControlRules,
      userType,
      module,
      elementId,
      defaultHidden ? "hidden" : "visible",
    );
  }, [
    module,
    userType,
    fieldControlRules,
    accessControlRules,
    elementId,
    defaultHidden,
  ]);

  if (state === "hidden") {
    return null;
  }

  if (state === "disabled") {
    // For valid React elements, try to clone with disabled/readOnly
    if (isValidElement(children)) {
      const child = children as ReactElement<Record<string, unknown>>;
      const elementType = typeof child.type === "string" ? child.type : null;

      // Non-form elements (div, span, etc.) — wrap with disabled styling
      if (
        elementType &&
        !["button", "input", "select", "textarea"].includes(elementType)
      ) {
        return (
          <div
            style={{ pointerEvents: "none", opacity: 0.5 }}
            aria-disabled="true"
          >
            {children}
          </div>
        );
      }

      // Form elements — clone with disabled/readOnly props
      return cloneElement(child, {
        disabled: true,
        readOnly: true,
      });
    }

    // Fallback: wrap non-element children
    return (
      <div style={{ pointerEvents: "none", opacity: 0.5 }} aria-disabled="true">
        {children}
      </div>
    );
  }

  // Visible: render as-is
  return <>{children}</>;
}
